import {
  WSAlert,
  WSAlertAction,
  WSAlertProps,
  WSElement,
  WSFlexBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { WorkLog, WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";

type AlertConfig = Pick<WSAlertProps, "theme" | "icon" | "title"> & {
  message: React.ReactNode;
  action?: WSAlertAction;
};

type Props = {
  workLog: WorkLog | undefined;
  onCancelEditingRequest?: () => void;
  onViewHistory?: () => void;
  clientName?: string;
};

const InlineComment: React.FC<{
  label: string;
  comment: string;
  date?: Date | string;
}> = ({ label, comment, date }) => (
  <WSElement mt="M" colorBackground="white" p="M" style={{ borderRadius: 2 }}>
    <WSFlexBox.CenterY wrap="wrap" gap="S">
      <WSText.ParagraphXs color="gray500">{label}</WSText.ParagraphXs>
      {date && (
        <>
          <WSElement
            colorBackground="gray400"
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0
            }}
          />
          <WSText.ParagraphXs color="gray500">
            {toWSDateString(date, "monthDayYear")}
          </WSText.ParagraphXs>
        </>
      )}
    </WSFlexBox.CenterY>
    <WSText.ParagraphXs color="gray600" py="S">
      &ldquo;{comment}&rdquo;
    </WSText.ParagraphXs>
  </WSElement>
);

export const AlertWorkLogReviewStatusPayee: React.FC<Props> = ({
  workLog,
  onCancelEditingRequest,
  onViewHistory,
  clientName
}) => {
  const history = useHistory();

  const payerComment = workLog?.payerOwnedData?.comment ?? undefined;
  const payeeComment = workLog?.payeeOwnedData?.comment ?? undefined;

  // TODO(v3): comment timestamp now read from owned-data `commentAt` (v1 used the
  // work log's top-level `updatedAt`, which no longer exists).
  const commentDate =
    (payerComment
      ? workLog?.payerOwnedData?.commentAt
      : workLog?.payeeOwnedData?.commentAt) ?? undefined;

  // Only one comment exists at a time
  const comment = payerComment || payeeComment;
  const commentLabel = payerComment
    ? `Comment from ${clientName || "client"}`
    : "Your comment";

  const alertConfigs = useMemo((): AlertConfig[] => {
    const configs: AlertConfig[] = [];

    switch (workLog?.status) {
      case WorkLogStatus.DRAFT:
        break;
      case WorkLogStatus.APPROVED:
        configs.push({
          theme: "success",
          icon: "check-circle",
          title: "Work log approved",
          message: (
            <>
              Work log was approved and an invoice was generated.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          ),
          action: workLog.convertedResourceId
            ? {
                text: "View invoice",
                onClick: () =>
                  history.push(
                    `/member/invoices/${workLog.convertedResourceId}`
                  )
              }
            : undefined
        });
        break;
      case WorkLogStatus.IN_REVIEW:
        if (payeeComment) {
          configs.push({
            theme: "neutral",
            icon: "time",
            title: "Awaiting client response",
            message: (
              <>
                You have requested that the submitted work log be returned for
                editing. The client can respond to the request by returning the
                work log back to you for corrections, make edits themselves, or
                approve it as-is. You&apos;ll be notified of their decision.
                <InlineComment
                  label={commentLabel}
                  comment={payeeComment}
                  date={commentDate}
                />
              </>
            ),
            action: onCancelEditingRequest
              ? {
                  text: "Cancel request for editing",
                  onClick: onCancelEditingRequest
                }
              : undefined
          });
        } else {
          configs.push({
            theme: "neutral",
            icon: "info-circle",
            title: "Awaiting client approval",
            message: (
              <>
                Your client will review and either approve this work log or
                request changes. You&apos;ll be notified of their decision. Work
                logs cannot be edited while in review. Need to make corrections?
                Use{" "}
                <WSText.ParagraphSm inline weight="medium">
                  &quot;Request editing&quot;
                </WSText.ParagraphSm>{" "}
                above to ask the client to send the work log back to you for
                editing.
              </>
            )
          });
        }
        break;
      case WorkLogStatus.IN_PROGRESS:
        configs.push({
          theme: "warning",
          icon: "alert",
          title: "Action required",
          message: (
            <>
              The work log was returned to you for editing. Once the work log is
              ready, resubmit it for client approval.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          )
        });
        break;
      case WorkLogStatus.REJECTED:
        configs.push({
          theme: "error",
          icon: "block",
          title: "Rejected",
          message: (
            <>
              The work log was rejected and was not invoiced.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          ),
          action: onViewHistory
            ? {
                text: "View history",
                onClick: onViewHistory
              }
            : undefined
        });
        break;
    }

    return configs;
  }, [
    workLog,
    history,
    comment,
    commentDate,
    commentLabel,
    payeeComment,
    onCancelEditingRequest,
    onViewHistory
  ]);

  if (alertConfigs.length === 0) {
    return null;
  }

  return (
    <WSList gap="M">
      {alertConfigs.map(({ message, ...alertProps }, index) => (
        <WSAlert key={index} {...alertProps}>
          {message}
        </WSAlert>
      ))}
    </WSList>
  );
};
